import { useState, useRef } from 'react';
import {
    Box,
    Button,
    Card,
    Typography,
    LinearProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Chip,
    Stack
} from '@mui/material';
import { CloudUpload, PlayArrow, CheckCircle, Error as ErrorIcon, Refresh } from '@mui/icons-material';
import { useDevice } from '~/lib/auth/device-context';
import { earnPoints } from '~/lib/api/api-client';

interface TransactionRecord {
    id: number;
    barcode: string;
    amount: number;
    status: 'pending' | 'success' | 'error';
    message?: string;
}

export function BulkTransactionImport() {
    const { deviceToken, cashierName, isRegistered } = useDevice();
    const [file, setFile] = useState<File | null>(null);
    const [records, setRecords] = useState<TransactionRecord[]>([]);
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const selectedFile = e.target.files[0];
            setFile(selectedFile);
            parseCSV(selectedFile);
        }
    };

    const parseCSV = (file: File) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target?.result as string;
            if (!text) return;

            const lines = text.split(/\r?\n/);
            const parsedRecords: TransactionRecord[] = [];
            
            lines.forEach((line, index) => {
                const trimmedLine = line.trim();
                if (!trimmedLine) return; // Skip empty lines
                
                // Skip header if present (simple check if first line has 'barcode' or 'amount')
                if (index === 0 && (trimmedLine.toLowerCase().includes('barcode') || trimmedLine.toLowerCase().includes('amount'))) {
                    return;
                }

                const parts = trimmedLine.split(',');
                if (parts.length >= 2) {
                    const barcode = parts[0].trim();
                    const amount = parseFloat(parts[1].trim());

                    if (barcode && !isNaN(amount)) {
                        parsedRecords.push({
                            id: index,
                            barcode,
                            amount,
                            status: 'pending'
                        });
                    }
                }
            });

            setRecords(parsedRecords);
            setProgress(0);
        };
        reader.readAsText(file);
    };

    const processBatch = async () => {
        if (!deviceToken || !cashierName) {
            alert("الجهاز غير مسجل. يرجى تسجيل الجهاز أولاً.");
            return;
        }

        setIsProcessing(true);
        const batchSize = 5; // Process 5 at a time
        const total = records.length;
        let processed = 0;

        // Create a copy of records to update status
        const currentRecords = [...records];

        for (let i = 0; i < total; i += batchSize) {
            const batch = currentRecords.slice(i, i + batchSize);
            
            await Promise.all(batch.map(async (record) => {
                if (record.status === 'success') return; // Skip already successful

                try {
                    const result = await earnPoints({
                        barcode: record.barcode,
                        amount: record.amount,
                        cashier_name: cashierName,
                        deviceToken: deviceToken,
                        notes: "Bulk Import"
                    });

                    if (result.success) {
                        record.status = 'success';
                        record.message = 'تمت الإضافة';
                    } else {
                        record.status = 'error';
                        record.message = result.error || 'فشل';
                    }
                } catch (err: any) {
                    record.status = 'error';
                    record.message = err.message || 'خطأ في الاتصال';
                }
            }));

            processed += batch.length;
            setProgress(Math.min((processed / total) * 100, 100));
            setRecords([...currentRecords]); // Update UI
        }

        setIsProcessing(false);
    };

    const reset = () => {
        setFile(null);
        setRecords([]);
        setProgress(0);
        setIsProcessing(false);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const stats = {
        total: records.length,
        success: records.filter(r => r.status === 'success').length,
        error: records.filter(r => r.status === 'error').length,
        pending: records.filter(r => r.status === 'pending').length
    };

    if (!isRegistered) {
        return (
            <Alert severity="warning">
                يجب تسجيل هذا الجهاز أولاً لتتمكن من استيراد المعاملات.
            </Alert>
        );
    }

    return (
        <Card variant="outlined" sx={{ p: 3 }}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6">استيراد معاملات (Bulk Import)</Typography>
                {records.length > 0 && (
                    <Button startIcon={<Refresh />} onClick={reset} disabled={isProcessing}>
                        إعادة تعيين
                    </Button>
                )}
            </Box>

            {!file ? (
                <Box
                    sx={{
                        border: '2px dashed',
                        borderColor: 'divider',
                        borderRadius: 2,
                        p: 4,
                        textAlign: 'center',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.hover' }
                    }}
                    onClick={() => fileInputRef.current?.click()}
                >
                    <input
                        type="file"
                        accept=".csv,.txt"
                        hidden
                        ref={fileInputRef}
                        onChange={handleFileChange}
                    />
                    <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                    <Typography>اضغط لاختيار ملف CSV</Typography>
                    <Typography variant="caption" color="text.secondary">
                        الصيغة: Barcode, Amount
                    </Typography>
                </Box>
            ) : (
                <Box>
                    <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                        <Typography variant="body2">
                            الملف: <strong>{file.name}</strong> ({records.length} سجل)
                        </Typography>
                        {!isProcessing && stats.pending > 0 && (
                            <Button
                                variant="contained"
                                startIcon={<PlayArrow />}
                                onClick={processBatch}
                                disabled={stats.pending === 0}
                            >
                                بدء الاستيراد
                            </Button>
                        )}
                    </Box>

                    {isProcessing && (
                        <Box sx={{ mb: 2 }}>
                            <LinearProgress variant="determinate" value={progress} />
                            <Typography variant="caption" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
                                {Math.round(progress)}%
                            </Typography>
                        </Box>
                    )}

                    <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                        <Chip label={`الكل: ${stats.total}`} />
                        <Chip label={`ناجح: ${stats.success}`} color="success" icon={<CheckCircle />} />
                        <Chip label={`فشل: ${stats.error}`} color="error" icon={<ErrorIcon />} />
                    </Stack>

                    <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>#</TableCell>
                                    <TableCell>الباركود</TableCell>
                                    <TableCell>المبلغ</TableCell>
                                    <TableCell>الحالة</TableCell>
                                    <TableCell>ملاحظات</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {records.map((row, idx) => (
                                    <TableRow key={idx} hover>
                                        <TableCell>{idx + 1}</TableCell>
                                        <TableCell>{row.barcode}</TableCell>
                                        <TableCell>{row.amount}</TableCell>
                                        <TableCell>
                                            {row.status === 'success' && <CheckCircle color="success" fontSize="small" />}
                                            {row.status === 'error' && <ErrorIcon color="error" fontSize="small" />}
                                            {row.status === 'pending' && <Typography variant="caption" color="text.secondary">انتظار</Typography>}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="caption" color={row.status === 'error' ? 'error' : 'text.secondary'}>
                                                {row.message}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}
        </Card>
    );
}
